import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, Button, Space, Typography } from 'antd';
import { BarChartOutlined, LineChartOutlined, PieChartOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Title } = Typography;

const ChartConfigModal = ({
  visible,
  onCancel,
  onSave,
  data = [],
  initialConfig = {}
}) => {
  const [form] = Form.useForm();
  const [availableFields, setAvailableFields] = useState([]);

  useEffect(() => {
    if (data && data.length > 0) {
      // Extract field names from data
      const fields = Object.keys(data[0]).map(key => ({
        name: key,
        label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
        type: typeof data[0][key] === 'number' ? 'number' : 'string'
      }));
      setAvailableFields(fields);
    }
  }, [data]);

  useEffect(() => {
    if (visible && initialConfig) {
      form.setFieldsValue(initialConfig);
    }
  }, [visible, initialConfig, form]);

  const chartTypes = [
    { value: 'bar', label: 'Bar Chart', icon: BarChartOutlined },
    { value: 'line', label: 'Line Chart', icon: LineChartOutlined },
    { value: 'pie', label: 'Pie Chart', icon: PieChartOutlined }
  ];

  const handleSave = () => {
    form.validateFields().then(values => {
      onSave && onSave(values);
      form.resetFields();
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel && onCancel();
  };

  const numericFields = availableFields.filter(field => field.type === 'number');

  return (
    <Modal
      title={
        <Space>
          <BarChartOutlined />
          Configure Chart
        </Space>
      }
      visible={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Save Chart
        </Button>
      ]}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          chartType: 'bar',
          title: 'Chart Title',
          ...initialConfig
        }}
      >
        <Form.Item
          name="chartType"
          label="Chart Type"
          rules={[{ required: true, message: 'Please select a chart type' }]}
        >
          <Select placeholder="Select chart type">
            {chartTypes.map(type => (
              <Option key={type.value} value={type.value}>
                <Space>
                  <type.icon />
                  {type.label}
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="title"
          label="Chart Title"
          rules={[{ required: true, message: 'Please enter a chart title' }]}
        >
          <Input placeholder="Enter chart title" />
        </Form.Item>

        <Form.Item
          name="xAxisField"
          label="X-Axis Field (Categories)"
          rules={[{ required: true, message: 'Please select X-axis field' }]}
        >
          <Select placeholder="Select field for X-axis">
            {availableFields.map(field => (
              <Option key={field.name} value={field.name}>
                {field.label} ({field.type})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="yAxisField"
          label="Y-Axis Field (Values)"
          rules={[{ required: true, message: 'Please select Y-axis field' }]}
        >
          <Select placeholder="Select numeric field for Y-axis">
            {numericFields.map(field => (
              <Option key={field.name} value={field.name}>
                {field.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="height"
          label="Chart Height (px)"
          rules={[{ required: true, message: 'Please enter chart height' }]}
        >
          <Select placeholder="Select chart height">
            <Option value={300}>Small (300px)</Option>
            <Option value={400}>Medium (400px)</Option>
            <Option value={500}>Large (500px)</Option>
            <Option value={600}>Extra Large (600px)</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChartConfigModal;